/**
 * Normalize responsibility deep links for in-app <Link> / navigate().
 *
 * RPC payloads sometimes omit the /prioritization prefix, use legacy /sessions/* paths,
 * or point at /prioritization/workspace which the router redirects to home.
 */

import type { ResponsibilityCard } from '../types/prioritization/responsibility';
import { getPrimaryAction } from '../types/prioritization/responsibility';

/** Router sends these straight back to prioritization home — unusable as action targets. */
const TRAP_PATHS = new Set(['/prioritization', '/prioritization/workspace']);

function stripTrailingSlashes(path: string): string {
  return path.replace(/\/+$/, '') || '/';
}

/**
 * Turn RPC/browser URLs into a pathname suitable for React Router `to` / `navigate`.
 */
import { rewritePathForMandateShell } from './productSurfacePaths';

export function normalizePrioritizationInternalPath(raw: string): string {
  let s = raw.trim();
  if (!s) return '/prioritization';

  if (s.startsWith('http://') || s.startsWith('https://')) {
    try {
      const u = new URL(s);
      s = u.pathname + u.search + u.hash;
    } catch {
      return '/prioritization';
    }
  }

  if (!s.startsWith('/')) s = `/${s}`;

  if (s.startsWith('/prioritization/') || s === '/prioritization') {
    return rewritePathForMandateShell(s);
  }

  if (
    s.startsWith('/sessions/') ||
    s === '/sessions' ||
    s.startsWith('/task/') ||
    s.startsWith('/my-tasks') ||
    s.startsWith('/cycles/') ||
    s.startsWith('/items/')
  ) {
    return `/prioritization${s}`;
  }

  const sessionTypo = /^\/session\/([^/?#]+)/.exec(s);
  if (sessionTypo) {
    const rest = s.slice(sessionTypo[0].length);
    return `/prioritization/sessions/${sessionTypo[1]}${rest}`;
  }

  return s;
}

function isHomeTrap(path: string): boolean {
  const p = stripTrailingSlashes(path);
  return p === '/' || TRAP_PATHS.has(p);
}

function scopeFallbackHref(card: ResponsibilityCard): string {
  if (card.scope === 'SESSION') {
    return `/prioritization/sessions/${encodeURIComponent(card.id)}`;
  }
  return '/prioritization/my-work';
}

/** First candidate that normalizes to a real destination (not `/` or legacy workspace → home). */
function firstUsableHref(candidates: (string | null | undefined)[]): string | null {
  for (const c of candidates) {
    const t = c != null ? String(c).trim() : '';
    if (!t) continue;
    const path = normalizePrioritizationInternalPath(t);
    if (!isHomeTrap(path)) return path;
  }
  return null;
}

/**
 * Preferred in-app href for a responsibility row (dashboard widget, queues, etc.).
 *
 * @param preferredRaw — optional resolved URL (e.g. catchball workbench) tried first, then
 *   primary action route, then deep_link. Skips values that normalize to the workspace/home trap.
 */
export function getResponsibilityNavigationHref(
  card: ResponsibilityCard,
  preferredRaw?: string | null
): string | null {
  const primary = getPrimaryAction(card);
  const picked = firstUsableHref([preferredRaw, primary?.route, card.deep_link]);
  if (picked) return picked;

  if (card.classification === 'REQUIRED') {
    return scopeFallbackHref(card);
  }
  return null;
}
